import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Listen-Parameter (Seite, Seitengröße, Sortierung, Suche, Filter) in der URL –
 * so bleiben Filter beim Zurücknavigieren erhalten und Links sind teilbar.
 */
export function useListParams<F extends Record<string, string>>(defaults: { pageSize?: number; sort?: string; order?: 'asc' | 'desc'; filters?: F } = {}) {
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get('page') ?? 1) || 1;
  const pageSize = Number(sp.get('pageSize') ?? defaults.pageSize ?? 25) || 25;
  const sort = sp.get('sort') ?? defaults.sort;
  const order = (sp.get('order') as 'asc' | 'desc' | null) ?? defaults.order ?? 'asc';
  const q = sp.get('q') ?? '';
  const filters = useMemo(() => {
    const f: Record<string, string> = { ...(defaults.filters ?? {}) };
    for (const k of Object.keys(defaults.filters ?? {})) {
      const v = sp.get(k);
      if (v !== null) f[k] = v;
    }
    return f as F;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const update = useCallback(
    (patch: Record<string, string | number | undefined | null>, resetPage = true) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          if (resetPage && !('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  return {
    page,
    pageSize,
    sort,
    order,
    q,
    filters,
    setPage: (p: number) => update({ page: p }, false),
    setPageSize: (s: number) => update({ pageSize: s }),
    setSort: (s: { sort?: string; order: 'asc' | 'desc' }) => update({ sort: s.sort, order: s.order }),
    setQ: (v: string) => update({ q: v }),
    setFilter: (k: keyof F & string, v: string) => update({ [k]: v }),
    query: { page, pageSize, sort, order, q: q || undefined, ...filters },
  };
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Such-Eingabe mit Verzögerung, synchronisiert mit URL-Parameter q */
export function useSearchBox(q: string, setQ: (v: string) => void, ms = 350) {
  const [text, setText] = useState(q);
  const debounced = useDebounced(text, ms);
  useEffect(() => {
    if (debounced !== q) setQ(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => {
    setText(q);
  }, [q]);
  return [text, setText] as const;
}
